/** Scheduled-transaction dates (scheduled-transactions.md §2). Pure; occurrences always count from dateFirst. */
import { addDays, daysInMonth, type IsoDate } from './dates';

/** YNAB's frequency set, so the importer maps 1:1. */
export const FREQUENCIES = [
  'never',
  'daily',
  'weekly',
  'everyOtherWeek',
  'twiceAMonth',
  'every4Weeks',
  'monthly',
  'everyOtherMonth',
  'every3Months',
  'every4Months',
  'twiceAYear',
  'yearly',
  'everyOtherYear',
] as const;
export type Frequency = (typeof FREQUENCIES)[number];

export const FREQUENCY_LABELS: Record<Frequency, string> = {
  never: "Doesn't repeat",
  daily: 'Every day',
  weekly: 'Every week',
  everyOtherWeek: 'Every other week',
  twiceAMonth: 'Twice a month',
  every4Weeks: 'Every 4 weeks',
  monthly: 'Every month',
  everyOtherMonth: 'Every other month',
  every3Months: 'Every 3 months',
  every4Months: 'Every 4 months',
  twiceAYear: 'Twice a year',
  yearly: 'Every year',
  everyOtherYear: 'Every other year',
};

const DAY_STEP: Partial<Record<Frequency, number>> = {
  daily: 1,
  weekly: 7,
  everyOtherWeek: 14,
  every4Weeks: 28,
};
const MONTH_STEP: Partial<Record<Frequency, number>> = {
  monthly: 1,
  everyOtherMonth: 2,
  every3Months: 3,
  every4Months: 4,
  twiceAYear: 6,
  yearly: 12,
  everyOtherYear: 24,
};

const pad = (n: number) => String(n).padStart(2, '0');

/** Same day-of-month `n` months later, clamped to the month end (31 Jan + 1 → 28/29 Feb). */
export function addMonthsClamped(date: IsoDate, n: number): IsoDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const month = `${ny}-${pad(nm)}`;
  return `${month}-${pad(Math.min(d, daysInMonth(month)))}`;
}

/** The n-th occurrence (0-based), or null when the frequency has no such occurrence. */
export function occurrence(dateFirst: IsoDate, frequency: Frequency, n: number): IsoDate | null {
  if (n < 0) return null;
  if (frequency === 'never') return n === 0 ? dateFirst : null;
  const days = DAY_STEP[frequency];
  if (days) return addDays(dateFirst, days * n);
  const months = MONTH_STEP[frequency];
  if (months) return addMonthsClamped(dateFirst, months * n);
  // twiceAMonth: the day of dateFirst, and 15 days after it, every month.
  const base = addMonthsClamped(dateFirst, Math.floor(n / 2));
  return n % 2 === 0 ? base : addDays(base, 15);
}

/** Index of the first occurrence on or after `date` (null when there is none). */
export function occurrenceIndexFrom(dateFirst: IsoDate, frequency: Frequency, date: IsoDate): number | null {
  if (date <= dateFirst) return 0;
  // Jump close to the target, then walk; occurrences are monotonic.
  const span = (Date.parse(date + 'T00:00:00Z') - Date.parse(dateFirst + 'T00:00:00Z')) / 86_400_000;
  const days = DAY_STEP[frequency];
  const months = MONTH_STEP[frequency];
  let n = days
    ? Math.max(0, Math.floor(span / days) - 1)
    : months
      ? Math.max(0, Math.floor(span / (months * 31)) - 1)
      : frequency === 'twiceAMonth'
        ? Math.max(0, Math.floor(span / 31) * 2 - 2)
        : 0;
  for (let guard = 0; guard < 10_000; guard++, n++) {
    const d = occurrence(dateFirst, frequency, n);
    if (d === null) return null;
    if (d >= date) return n;
  }
  return null;
}

/** First occurrence strictly after `date`, respecting `endDate`. */
export function nextOccurrenceAfter(
  dateFirst: IsoDate,
  frequency: Frequency,
  date: IsoDate,
  endDate?: IsoDate | null,
): IsoDate | null {
  const n = occurrenceIndexFrom(dateFirst, frequency, addDays(date, 1));
  if (n === null) return null;
  const d = occurrence(dateFirst, frequency, n);
  if (!d || (endDate && d > endDate)) return null;
  return d;
}

/** Occurrences from `from` (inclusive) up to `to` (inclusive), at most `limit`. */
export function occurrencesBetween(
  dateFirst: IsoDate,
  frequency: Frequency,
  from: IsoDate,
  to: IsoDate,
  endDate?: IsoDate | null,
  limit = 400,
): IsoDate[] {
  const out: IsoDate[] = [];
  let n = occurrenceIndexFrom(dateFirst, frequency, from);
  while (n !== null && out.length < limit) {
    const d = occurrence(dateFirst, frequency, n);
    if (!d || d > to || (endDate && d > endDate)) break;
    out.push(d);
    n++;
  }
  return out;
}
